/**
 * useConnectorCredentials -- per-connector credential store (P213.2).
 *
 * GET  /v1/admin/twin/connectors/{id}/credentials          (presence only)
 * PUT  /v1/admin/twin/connectors/{id}/credentials/{field}  (set; encrypted)
 * DELETE .../credentials/{field}                           (clear)
 *
 * The GET never returns secret values -- only presence (stored / env /
 * not_set). The PUT body carries the plaintext once (write-only); the
 * backend encrypts it with Fernet. Admin-gated; callers gate the UI with
 * useMe().
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type {
  ConnectorCredentialsResponse,
  ConnectorTestResult,
} from "shared/api/types";

const key = (connectorId: string) =>
  ["admin-connector-credentials", connectorId] as const;

export function useConnectorCredentials(
  connectorId: string | null,
  options: { enabled?: boolean } = {},
): UseQueryResult<ConnectorCredentialsResponse> {
  return useQuery<ConnectorCredentialsResponse>({
    queryKey: key(connectorId ?? ""),
    queryFn: () =>
      api.get<ConnectorCredentialsResponse>(
        `/v1/admin/twin/connectors/${connectorId}/credentials`,
      ),
    enabled: (options.enabled ?? true) && Boolean(connectorId),
  });
}

export function useSetConnectorCredential(): UseMutationResult<
  ConnectorCredentialsResponse,
  Error,
  { connectorId: string; fieldName: string; value: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ connectorId, fieldName, value }) =>
      api.put<ConnectorCredentialsResponse>(
        `/v1/admin/twin/connectors/${connectorId}/credentials/${encodeURIComponent(
          fieldName,
        )}`,
        { value },
      ),
    onSuccess: (data, { connectorId }) => {
      qc.setQueryData(key(connectorId), data);
      void qc.invalidateQueries({ queryKey: key(connectorId) });
    },
  });
}

/** P213.3: dry-run a connector's connection + auth (read-only). */
export function useTestConnector(): UseMutationResult<
  ConnectorTestResult,
  Error,
  string
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (connectorId: string) =>
      api.post<ConnectorTestResult>(
        `/v1/admin/twin/connectors/${connectorId}/test`,
        {},
      ),
  });
}

export function useClearConnectorCredential(): UseMutationResult<
  ConnectorCredentialsResponse,
  Error,
  { connectorId: string; fieldName: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ connectorId, fieldName }) =>
      api.delete<ConnectorCredentialsResponse>(
        `/v1/admin/twin/connectors/${connectorId}/credentials/${encodeURIComponent(
          fieldName,
        )}`,
      ),
    onSuccess: (data, { connectorId }) => {
      qc.setQueryData(key(connectorId), data);
      void qc.invalidateQueries({ queryKey: key(connectorId) });
    },
  });
}
